const YOUTUBE_ID_REGEX = /^[A-Za-z0-9_-]{11}$/;

const ALLOWED_YOUTUBE_HOSTS = new Set([
  'youtube.com',
  'www.youtube.com',
  'm.youtube.com',
  'music.youtube.com',
  'youtu.be'
]);

export class YoutubeValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'YoutubeValidationError';
  }
}

const coerceYoutubeUrl = (value: string): URL => {
  try {
    return new URL(value);
  } catch {
    try {
      return new URL(`https://${value}`);
    } catch {
      throw new YoutubeValidationError('Enter a valid YouTube URL.');
    }
  }
};

const parseYoutubeTimestampParam = (value: string | null): number | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed.length) return null;

  const normalized = trimmed.toLowerCase();
  const timeParts = normalized.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?/);
  if (timeParts && (timeParts[1] || timeParts[2] || timeParts[3])) {
    const hours = timeParts[1] ? Number.parseInt(timeParts[1], 10) : 0;
    const minutes = timeParts[2] ? Number.parseInt(timeParts[2], 10) : 0;
    const seconds = timeParts[3] ? Number.parseInt(timeParts[3], 10) : 0;
    if ([hours, minutes, seconds].some((n) => Number.isNaN(n))) {
      throw new YoutubeValidationError('Invalid timestamp value.');
    }
    return Math.round((hours * 3600 + minutes * 60 + seconds) * 1000);
  }

  if (/^\d+$/.test(trimmed)) {
    return Math.round(Number.parseInt(trimmed, 10) * 1000);
  }

  if (/^\d+\.\d+$/.test(trimmed)) {
    return Math.round(Number.parseFloat(trimmed) * 1000);
  }

  return null;
};

const parseColonSeparatedTime = (value: string): number | null => {
  const segments = value.split(':');
  if (segments.length < 2 || segments.length > 3) {
    return null;
  }
  const nums = segments.map((segment) => Number.parseFloat(segment));
  if (nums.some((n) => Number.isNaN(n))) {
    throw new YoutubeValidationError('Invalid timestamp value.');
  }

  let seconds = 0;
  while (nums.length) {
    const part = nums.pop() ?? 0;
    const multiplier = segments.length - nums.length - 1;
    seconds += part * Math.pow(60, multiplier);
  }

  return Math.round(seconds * 1000);
};

export const parseTimestampInput = (
  value: string | number | null | undefined,
  field: 'start' | 'end'
): number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      throw new YoutubeValidationError(`${field} must be a finite number.`);
    }
    if (value < 0) {
      throw new YoutubeValidationError(`${field} must be greater than or equal to 0.`);
    }
    return Math.round(value * 1000);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.length) return null;

    if (/^\d+$/.test(trimmed)) {
      return Math.round(Number.parseInt(trimmed, 10) * 1000);
    }
    if (/^\d+\.\d+$/.test(trimmed)) {
      return Math.round(Number.parseFloat(trimmed) * 1000);
    }

    const colon = parseColonSeparatedTime(trimmed);
    if (colon !== null) {
      return colon;
    }

    const param = parseYoutubeTimestampParam(trimmed);
    if (param !== null) {
      return param;
    }
  }

  throw new YoutubeValidationError('Unsupported timestamp format.');
};

export interface ParsedYoutubeUrl {
  videoId: string;
  startMsFromParam: number | null;
}

export const parseYoutubeUrl = (value: string): ParsedYoutubeUrl => {
  const url = coerceYoutubeUrl(value);
  const host = url.hostname.toLowerCase();
  if (!ALLOWED_YOUTUBE_HOSTS.has(host)) {
    throw new YoutubeValidationError('Please use a youtube.com or youtu.be link.');
  }

  let videoId: string | null = null;
  if (host === 'youtu.be') {
    const path = url.pathname.replace(/^\/+/, '').split('/')[0] ?? '';
    videoId = path.trim();
  } else {
    const path = url.pathname.replace(/\/+$/, '');
    if (url.searchParams.has('v')) {
      videoId = (url.searchParams.get('v') ?? '').trim();
    } else if (path.startsWith('/embed/')) {
      videoId = path.split('/')[2] ?? '';
    } else if (path.startsWith('/shorts/')) {
      const [, maybeId] = path.split('/shorts/');
      videoId = (maybeId ?? '').split('/')[0] ?? '';
    } else if (path.startsWith('/watch/')) {
      videoId = path.split('/')[2] ?? '';
    }
  }

  if (!videoId) {
    throw new YoutubeValidationError('Unable to extract a YouTube video id from this URL.');
  }
  if (!YOUTUBE_ID_REGEX.test(videoId)) {
    throw new YoutubeValidationError('Invalid YouTube video id.');
  }

  const start = parseYoutubeTimestampParam(url.searchParams.get('t'));
  return {
    videoId,
    startMsFromParam: start
  };
};

export const extractYoutubeVideoId = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed.length) {
    throw new YoutubeValidationError('Enter a YouTube URL or video id.');
  }
  if (YOUTUBE_ID_REGEX.test(trimmed)) {
    return trimmed;
  }
  return parseYoutubeUrl(trimmed).videoId;
};

export const sanitizeNotes = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
};
